/**
 * API Path Definitions for ADDAPT
 * This file defines API paths and structures.
 * Actual endpoint URLs come from settings.json which is loaded at runtime.
 * 
 * Why this separation?
 * - settings.json: Environment-specific URLs (dev/beta/gamma/prod)
 * - This file: API path structure that remains constant across environments
 */

export const API_PATHS = {
  // Profile Management
  PROFILES: {
    LIST: '/ListProfiles',              // ✅ Implemented
    GET: '/GetProfile',                 // ✅ Implemented
    CREATE: '/CreateProfile',           // ✅ Implemented
    LDAP_GROUPS: '/GetUserLdapGroups',  // ✅ Implemented - Get user's LDAP groups
  },
  
  // Job Management
  JOBS: {
    LIST: '/ListJobs',                  // ✅ Implemented - List jobs for a profile
    LIST_RUNS: '/ListJobRuns',          // ✅ Implemented - List runs for a job
    GET: '/GetJob',                     // ✅ Implemented - Get job details
    GET_DRAFT: '/GetDraftJob',          // ✅ Implemented - Create draft job with 1-hour TTL
    CREATE: '/CreateJob',               // ✅ Implemented - Convert draft to release job
    EDIT: '/EditJob',                   // ✅ Implemented - Update job details
    RUN: '/RunJob',                     // ✅ Implemented - Run a job
    RUN_STATUS: '/GetJobRunStatus',     // ✅ Implemented - Get job run status
  },
  
  // Template Management
  TEMPLATES: {
    GENERATE: '/GenerateTemplate',      // ✅ Implemented - Template generation
    STATUS: '/GetTemplateStatus',       // ✅ Implemented - Check template generation status
  },
  
  // Document Processing (S3)
  DOCUMENTS: {
    PRESIGNED_UPLOAD: '/GetPresignedUploadUrl',     // ✅ Implemented - Upload files to S3
    PRESIGNED_DOWNLOAD: '/GetPresignedDownloadUrl', // ✅ Implemented - Download files from S3
  },
  
  // Test endpoint
  TEST: {
    GET_ALL_BEERS: '/GetAllBeers',      // ✅ Used in App.tsx
  }
};

/**
 * Helper function to build full API URL
 * @param path - The API path from API_PATHS
 * @param params - Parameters to replace in the path
 * @returns Full URL with parameters replaced
 */
export function buildApiUrl(path: string, params?: Record<string, string>): string {
  let url = path;
  
  // Replace path parameters
  if (params) {
    Object.keys(params).forEach(key => {
      url = url.replace(`:${key}`, params[key]);
    });
  }
  
  return url;
}

/**
 * Get WebSocket URL from settings
 * @param type - 'default' or 'ai'
 * @returns WebSocket URL or null if not configured
 */
export function getWebSocketUrl(type: 'default' | 'ai' = 'default'): string | null {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  return settings?.webSocketConfig?.[type] || null;
}

/**
 * Get AI HTTP endpoint from settings
 * @returns AI HTTP endpoint URL or null if not configured
 */
export function getAIHttpEndpoint(): string | null {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  return settings?.aiHttpEndpoint || null;
}

/**
 * Check if a feature is enabled
 * @param feature - Feature name
 * @returns boolean indicating if feature is enabled
 */
export function isFeatureEnabled(feature: string): boolean {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  return settings?.features?.[feature] === true;
}